import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import AddressModule from "../../profile/Adresse";
import BankModule from "../../profile/BankModule";
import { CONTACT_FIELD_LABELS } from "../../../utils/contactValidation";
import { normalizeTaxNumberInput } from "../../../utils/taxNumber";
import { normalizeVatIdInput } from "../../../utils/vatId";

export default function CustomerDialog({
    open,
    onClose,
    onSave,
    editingIndex,
    currentCustomer,
    fieldErrors,
    taxErrors,
    taxNumberError,
    vatIdError,
    addressErrors,
    bankErrors,
    onFieldChange,
    onTaxChange,
    onAddressChange,
    onBankChange,
}) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle>
                {editingIndex !== null ? "Kunde bearbeiten" : "Kunde hinzufügen"}
                <IconButton aria-label="Schließen" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                        <TextField
                            label="Vorname / Firmenname"
                            value={currentCustomer.name}
                            onChange={(event) => onFieldChange("name", event.target.value)}
                            fullWidth
                            error={Boolean(fieldErrors.name)}
                            helperText={fieldErrors.name || ""}
                        />
                        <TextField
                            label="Nachname / Firmenname"
                            value={currentCustomer.lastname}
                            onChange={(event) => onFieldChange("lastname", event.target.value)}
                            fullWidth
                            error={Boolean(fieldErrors.lastname)}
                            helperText={fieldErrors.lastname || ""}
                        />
                    </Stack>

                    <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                        <TextField
                            label={CONTACT_FIELD_LABELS.email}
                            type="email"
                            value={currentCustomer.email}
                            onChange={(event) => onFieldChange("email", event.target.value)}
                            fullWidth
                            error={Boolean(fieldErrors.email)}
                            helperText={fieldErrors.email || ""}
                        />
                        <TextField
                            label={CONTACT_FIELD_LABELS.phone}
                            value={currentCustomer.phone}
                            onChange={(event) => onFieldChange("phone", event.target.value)}
                            fullWidth
                            error={Boolean(fieldErrors.phone)}
                            helperText={fieldErrors.phone || ""}
                        />
                    </Stack>

                    <Divider />

                    <AddressModule
                        addresses={currentCustomer.addresses}
                        onAddressChange={onAddressChange}
                        showOutgoingCheckbox={false}
                        validationErrors={addressErrors}
                    />

                    <Divider />

                    <BankModule
                        bankConnections={currentCustomer.bankConnections}
                        onBankChange={onBankChange}
                        showOutgoingCheckbox={false}
                        validationErrors={bankErrors}
                    />

                    <Divider />

                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                            Steuerdaten
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                            <TextField
                                label="Steuernummer"
                                value={currentCustomer?.taxData?.taxNumber || ""}
                                onChange={(event) => onTaxChange({ taxNumber: normalizeTaxNumberInput(event.target.value) })}
                                placeholder="z. B. 12/345/67890"
                                error={Boolean(taxErrors.taxNumber || taxNumberError)}
                                helperText={taxErrors.taxNumber || taxNumberError || ""}
                                fullWidth
                            />
                            <TextField
                                label="Wirtschafts-ID"
                                value={currentCustomer?.taxData?.economicId || ""}
                                onChange={(event) => onTaxChange({ economicId: event.target.value })}
                                error={Boolean(taxErrors.economicId)}
                                helperText={taxErrors.economicId || ""}
                                fullWidth
                            />
                            <TextField
                                label="Umsatzsteuer-ID"
                                value={currentCustomer?.taxData?.ustId || currentCustomer?.taxData?.vatNumber || ""}
                                onChange={(event) => {
                                    const normalized = normalizeVatIdInput(event.target.value);
                                    onTaxChange({ ustId: normalized, vatNumber: normalized });
                                }}
                                placeholder="z. B. DE123456789"
                                error={Boolean(taxErrors.ustId || taxErrors.vatNumber || vatIdError)}
                                helperText={taxErrors.ustId || taxErrors.vatNumber || vatIdError || ""}
                                fullWidth
                            />
                        </Stack>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Abbrechen
                </Button>
                <Button
                    onClick={onSave}
                    variant="contained"
                    sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, color: "#000" }}
                >
                    Speichern
                </Button>
            </DialogActions>
        </Dialog>
    );
}
